// AgentVerse/client/ui/UserManager.js
import { ComponentBase } from "../core/ComponentBase.js";
import { RpcDataProvider } from "../core/RpcDataProvider.js";
import { DataView } from "../components/DataView.js"; // Let ComponentManager handle this, but good for reference
import { avatarHelper } from "../../helpers/avatarHelper.js";
import { z } from "zod";

export class UserManager extends ComponentBase {
    constructor(config) {
        super(config);
        this.dataView = null;
        this.dataProvider = null;
        this.eventGroup = `${this.config.instanceId || 'userManager'}-view`;
    }

    async initialize() {
        // No project context is needed since this is an admin-level component managing all users.
        this.dataProvider = new RpcDataProvider({
            appClient: this.appClient,
            eventGroup: `${this.eventGroup}-provider`,
            rpcClass: "UserManagerRpc", // Use the dedicated Admin RPC
            pageSize: 25,
            autoLoad: false, // We'll load data after login
            resolveReferences: false,
        });
        await this.dataProvider.initialize();

        this.dataView = await this.createManagedComponent("DataView", null, {
            eventGroup: this.eventGroup,
            appClient: this.appClient,
            dataProvider: this.dataProvider,
            title: "User Management",
            viewMode: 'grid', // Initial view mode
            fields: [
                {
                    name: 'profilePicture', label: 'Avatar',
                    render: (value, item) => {
                        const avatarContainer = document.createElement('div');
                        const avatar = avatarHelper.createAvatarElement({
                            displayName: item.username,
                            avatarUrl: value, // value is the profilePicture path
                            sizeClass: 'small'
                        });
                        avatarContainer.appendChild(avatar);
                        return avatarContainer.innerHTML;
                    }
                },
                { name: 'username', label: 'Username', render: (value) => `<strong>${value}</strong>` },
                { name: 'email', label: 'Email' },
                { name: 'firstName', label: 'First Name' },
                { name: 'role', label: 'Role', render: (value) => `<span class="badge bg-info text-dark">${value}</span>` },
                { name: 'isActive', label: 'Active', render: (value) => value ? '<i class="bi bi-check-circle-fill text-success"></i>' : '<i class="bi bi-x-circle-fill text-danger"></i>' }
            ],
            filter: {
                enabled: true,
                schema: z.object({
                    username: z.string().optional(),
                    email: z.string().optional(),
                    role: z.string().optional(),
                }),
                fields: {
                    username: { type: 'text', label: 'Filter by Username' },
                    email: { type: 'text', label: 'Filter by Email' },
                    role: {
                        type: 'select', label: 'Filter by Role',
                        options: [
                            { value: '', label: 'All Roles' },
                            { value: 'user', label: 'User' },
                            { value: 'power-user', label: 'Power User' },
                            { value: 'admin', label: 'Admin' }
                        ]
                    }
                }
            },
            actions: [
                {
                    label: 'Configure', icon: 'bi-gear-fill',
                    eventName: 'configureUser',
                    className: 'btn-outline-primary btn-sm'
                },
                {
                    label: 'Delete', icon: 'bi-trash',
                    eventName: 'deleteItem', 
                    className: 'btn-outline-danger btn-sm'
                }
            ],
            toolbar: {
                enabled: true,
                create: {
                    enabled: true,
                    label: "Create New User",
                    icon: "bi-plus-lg"
                },
                refresh: { enabled: true },
                viewModes: { enabled: true }
            },
            autoLoad: false
        });

        // Listen for DataView's custom actions
        this.dataView.on(`configureUser`, (item) => {
            if (item && item._id) {
                this.appClient.layoutEngine.navigate(`/admin/user-config?userId=${item._id}`);
            }
        });
        
        // Listen for the toolbar's create button click
        this.dataView.on(`createItem`, () => {
            this.log.info("Create New User button clicked. Navigating...");
            // Navigate to the config view without a userId to trigger "create mode"
            this.appClient.layoutEngine.navigate(`/admin/user-config`);
        });

        // Listen for the generic delete action from DataView
        this.dataView.on(`deleteItem`, async (item) => {
            if (item && item._id) {
                const confirmed = await this.appClient.notifier.confirm(
                    this.t("Are you sure you want to delete user \"{{username}}\"?", { username: item.username }),
                    { title: this.t('Confirm Delete'), type: 'danger' }
                );
                if (confirmed) {
                    try {
                        await this.appClient.rpcClient.call('AdminUserRpc.delete', { _id: item._id });
                        this.appClient.notifier.success(this.t("User \"{{username}}\" deleted.", { username: item.username }));
                        await this.dataView.refreshData();
                    } catch (error) {
                        this.log.error("Error deleting user:", error);
                        this.appClient.notifier.error(this.t("Failed to delete user: {{errorMessage}}", { errorMessage: error.message }));
                    }
                }
            }
        });

        await super.initialize();
    }

    async initAfterLogin() {
        if (this.dataView) {
            await this.dataView.refreshData();
        }
    }

    async render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'user-manager-container h-100';
        }
        this.container.innerHTML = ''; // Clear previous content

        if (this.dataView) {
            this.container.appendChild(await this.dataView.render());
        } else {
            const loadingPlaceholder = document.createElement('div');
            loadingPlaceholder.textContent = this.t('Loading user manager...');
            this.container.appendChild(loadingPlaceholder);
            this.appClient.tx.applyTranslations(loadingPlaceholder);
        }
        return this.container;
    }

    destroy() {
        this.dataView?.destroy();
        this.dataProvider?.destroy();
        this.dataView = null;
        this.dataProvider = null;
        super.destroy();
    }
}